import { isAxiosError, type AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hook_au/useAuth';
import apiClient from './apiClient';
import {
  normalizeSuppliersResponse,
  type Supplier,
  type SupplierCreatePayload,
  type SupplierFilters,
  type SupplierPagination,
  type SupplierUpdatePayload,
} from '../types/suppliers';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

const PAGE_SIZES = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const normalizePageSize = (size: number) =>
  PAGE_SIZES.includes(size as (typeof PAGE_SIZES)[number]) ? size : DEFAULT_PAGE_SIZE;

const ERROR_MESSAGES = {
  unauthorized: 'La sesión expiró. Vuelve a iniciar sesión para continuar.',
  notFound: 'No encontramos el proveedor solicitado.',
  invalidCategory: 'No se puede asignar una categoría inactiva.',
  unknown: 'No pudimos conectar con el servicio de proveedores. Inténtalo de nuevo en unos segundos.',
  list: 'No pudimos cargar los proveedores. Inténtalo de nuevo en unos segundos.',
} as const;

const normalizeCategoryIds = (categoryIds?: string[]) => {
  if (!categoryIds?.length) return undefined;

  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const categoryId of categoryIds) {
    const trimmed = categoryId.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    normalized.push(trimmed);
  }

  return normalized.length > 0 ? normalized : undefined;
};

const normalizeFilters = (raw: SupplierFilters): SupplierFilters => ({
  search: raw.search?.trim() || undefined,
  active: raw.active,
  category_ids: normalizeCategoryIds(raw.category_ids),
  include_uncategorized: raw.include_uncategorized ? true : undefined,
});

const sameCategoryIds = (a?: string[], b?: string[]) => {
  const left = a ?? [];
  const right = b ?? [];
  return left.length === right.length && left.every((value, index) => value === right[index]);
};

const sameFilters = (a: SupplierFilters, b: SupplierFilters) =>
  a.search === b.search
  && a.active === b.active
  && a.include_uncategorized === b.include_uncategorized
  && sameCategoryIds(a.category_ids, b.category_ids);

const serializeSupplierParams = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null) {
          searchParams.append(key, String(item));
        }
      });
      continue;
    }

    searchParams.append(key, String(value));
  }

  return searchParams.toString();
};

export function useSuppliersApi(
  initialFilters: SupplierFilters = {},
  initialPagination: SupplierPagination = { page: 1, page_size: DEFAULT_PAGE_SIZE },
) {
  const { token } = useAuth();
  const [filters, setFilters] = useState<SupplierFilters>(initialFilters);
  const [pagination, setPagination] = useState<SupplierPagination>({
    ...initialPagination,
    page_size: normalizePageSize(initialPagination.page_size ?? DEFAULT_PAGE_SIZE),
    total: initialPagination.total ?? 0,
  });
  const [data, setData] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSequenceRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de proveedores');
      }
      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/suppliers${config.url ?? ''}`,
        });
        return response.data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 401) {
          throw new Error(ERROR_MESSAGES.unauthorized);
        }
        throw err;
      }
    },
    [token],
  );

  const fetchSuppliers = useCallback(
    async (overrides: { filters?: SupplierFilters; page?: number; page_size?: number } = {}) => {
      const requestId = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestId;
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setLoading(true);
      setError(null);

      const normalizedFilters = normalizeFilters({ ...filters, ...(overrides.filters ?? {}) });
      const filtersChanged = !sameFilters(filters, normalizedFilters);
      const nextPageSize = normalizePageSize(overrides.page_size ?? pagination.page_size);
      const nextPage = overrides.page ?? (filtersChanged ? 1 : pagination.page);
      const offset = (nextPage - 1) * nextPageSize;

      try {
        const resp = await request<any>({
          method: 'GET',
          url: '',
          params: { ...normalizedFilters, limit: nextPageSize, offset },
          paramsSerializer: { serialize: serializeSupplierParams },
          signal: abortController.signal,
        });
        const normalized = normalizeSuppliersResponse(resp);
        const computedPageSize = normalizePageSize(normalized.pagination.page_size ?? nextPageSize);
        const computedTotal = normalized.pagination.total ?? pagination.total ?? offset + normalized.items.length;

        if (requestId !== requestSequenceRef.current) {
          return normalized;
        }

        setData(normalized.items);
        setFilters((prev) => (sameFilters(prev, normalizedFilters) ? prev : normalizedFilters));
        setPagination((prev) => {
          const next = { ...prev, ...normalized.pagination, total: computedTotal, page: nextPage, page_size: computedPageSize };
          return (
            prev.page === next.page &&
            prev.page_size === next.page_size &&
            prev.total === next.total &&
            prev.total_pages === next.total_pages
          )
            ? prev
            : next;
        });

        return normalized;
      } catch (err) {
        if (isAxiosError(err) && err.code === 'ERR_CANCELED') {
          return normalizeSuppliersResponse({
            items: [],
            pagination: { total: 0, page: nextPage, page_size: nextPageSize, total_pages: 1 },
          });
        }
        const message = isAxiosError(err) && err.response?.status === 401
          ? ERROR_MESSAGES.unauthorized
          : ERROR_MESSAGES.list;
        if (requestId !== requestSequenceRef.current) {
          return normalizeSuppliersResponse({
            items: [],
            pagination: { total: 0, page: nextPage, page_size: nextPageSize, total_pages: 1 },
          });
        }
        setError(message);
        throw new Error(message);
      } finally {
        if (requestId === requestSequenceRef.current) {
          abortControllerRef.current = null;
          setLoading(false);
        }
      }
    },
    [request, filters, pagination.page, pagination.page_size],
  );

  useEffect(() => {
    if (!token) return;
    void fetchSuppliers({ filters, page: pagination.page, page_size: pagination.page_size });
  }, [token, filters, pagination.page, pagination.page_size, fetchSuppliers]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const goToPage = (page: number) => fetchSuppliers({ page });
  const refresh = () => fetchSuppliers();

  return { data, loading, error, pagination, filters, setFilters, setPagination, goToPage, refresh };
}

export function useSupplierActions() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de proveedores');
      }
      try {
        const response = await apiClient.request<Response>({
          ...config,
          url: `/suppliers${config.url ?? ''}`,
        });
        return response.data;
      } catch (err) {
        if (isAxiosError(err)) {
          const status = err.response?.status;
          const detail =
            typeof err.response?.data === 'object'
            && err.response?.data
            && 'detail' in err.response.data
            && typeof err.response.data.detail === 'string'
              ? err.response.data.detail
              : undefined;
          if (status === 401) throw new Error(ERROR_MESSAGES.unauthorized);
          if (status === 404) throw new Error(detail ?? ERROR_MESSAGES.notFound);
          if (status === 409) throw new Error(detail ?? ERROR_MESSAGES.unknown);
          if (status === 422) throw new Error(detail ?? ERROR_MESSAGES.invalidCategory);
        }
        throw new Error(ERROR_MESSAGES.unknown);
      }
    },
    [token],
  );

  const createSupplier = useCallback(
    (payload: SupplierCreatePayload) =>
      request<Supplier>({
        method: 'POST',
        url: '',
        data: payload,
      }),
    [request],
  );

  const updateSupplier = useCallback(
    (id: Supplier['id'], payload: SupplierUpdatePayload) =>
      request<Supplier>({
        method: 'PUT',
        url: `/${id}`,
        data: payload,
      }),
    [request],
  );

  const deleteSupplier = useCallback(
    (id: Supplier['id']) =>
      request<void>({
        method: 'DELETE',
        url: `/${id}`,
      }),
    [request],
  );

  return { createSupplier, updateSupplier, deleteSupplier };
}
